import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';
import {
  buildTreatmentPlanV3AssessmentMethodVisualFileFormData,
  buildTreatmentPlanV3AssessmentMethodVisualsFormData,
} from './utils';

import type {
  ITreatmentPlanV3AssessmentBehavior,
  ITreatmentPlanV3AssessmentMethod,
  ITreatmentPlanV3AssessmentMethodVisual,
  ITreatmentPlanV3AssessmentPage,
  ITreatmentPlanV3AssessmentReadinessItemUpdate,
  ITreatmentPlanV3AssessmentTemplates,
  CreateTreatmentPlanV3AssessmentBehaviorPayload,
  CreateTreatmentPlanV3AssessmentMethodPayload,
  CreateTreatmentPlanV3AssessmentMethodVisualsPayload,
  DeleteTreatmentPlanV3AssessmentBehaviorPayload,
  DeleteTreatmentPlanV3AssessmentMethodPayload,
  DeleteTreatmentPlanV3AssessmentMethodVisualPayload,
  GetTreatmentPlanV3AssessmentPagePayload,
  GetTreatmentPlanV3AssessmentTemplatesPayload,
  TreatmentPlanV3AssessmentPageThunkConfig,
  UpdateTreatmentPlanV3AssessmentBehaviorPayload,
  UpdateTreatmentPlanV3AssessmentMethodPayload,
  UpdateTreatmentPlanV3AssessmentMethodVisualFilePayload,
  UpdateTreatmentPlanV3AssessmentMethodVisualPayload,
  UpdateTreatmentPlanV3AssessmentPagePayload,
  UpdateTreatmentPlanV3AssessmentReadinessItemPayload,
} from './types';

export function createTreatmentPlanV3AssessmentThunks() {
  return {
    getTreatmentPlanV3AssessmentPage: getTreatmentPlanV3AssessmentPage(),
    getTreatmentPlanV3AssessmentTemplates: getTreatmentPlanV3AssessmentTemplates(),
    updateTreatmentPlanV3AssessmentPage: updateTreatmentPlanV3AssessmentPage(),
    createTreatmentPlanV3AssessmentBehavior: createTreatmentPlanV3AssessmentBehavior(),
    updateTreatmentPlanV3AssessmentBehavior: updateTreatmentPlanV3AssessmentBehavior(),
    deleteTreatmentPlanV3AssessmentBehavior: deleteTreatmentPlanV3AssessmentBehavior(),
    createTreatmentPlanV3AssessmentMethod: createTreatmentPlanV3AssessmentMethod(),
    updateTreatmentPlanV3AssessmentMethod: updateTreatmentPlanV3AssessmentMethod(),
    deleteTreatmentPlanV3AssessmentMethod: deleteTreatmentPlanV3AssessmentMethod(),
    updateTreatmentPlanV3AssessmentReadinessItem: updateTreatmentPlanV3AssessmentReadinessItem(),
    createTreatmentPlanV3AssessmentMethodVisuals: createTreatmentPlanV3AssessmentMethodVisuals(),
    updateTreatmentPlanV3AssessmentMethodVisual: updateTreatmentPlanV3AssessmentMethodVisual(),
    updateTreatmentPlanV3AssessmentMethodVisualFile: updateTreatmentPlanV3AssessmentMethodVisualFile(),
    deleteTreatmentPlanV3AssessmentMethodVisual: deleteTreatmentPlanV3AssessmentMethodVisual(),
  };
}

function shouldFetchAssessmentPage(
  { treatmentPlanId }: GetTreatmentPlanV3AssessmentPagePayload,
  { getState }: { getState: () => TreatmentPlanV3AssessmentPageThunkConfig['state'] },
) {
  const { assessmentPage, assessmentPageLoading, assessmentPageTreatmentPlanId } =
    getState().treatmentPlanV3;
  if (assessmentPageTreatmentPlanId !== treatmentPlanId) return true;
  return assessmentPage == null && !assessmentPageLoading;
}

function getTreatmentPlanV3AssessmentPage() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentPage,
    GetTreatmentPlanV3AssessmentPagePayload,
    TreatmentPlanV3AssessmentPageThunkConfig
  >(
    'treatmentPlanV3/getTreatmentPlanV3AssessmentPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/`,
        getAccessTokenSilently,
      }),
    { condition: shouldFetchAssessmentPage },
  );
}

function getTreatmentPlanV3AssessmentTemplates() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentTemplates,
    GetTreatmentPlanV3AssessmentTemplatesPayload
  >(
    'treatmentPlanV3/getTreatmentPlanV3AssessmentTemplates',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/templates/`,
        getAccessTokenSilently,
      }),
  );
}

function updateTreatmentPlanV3AssessmentPage() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentPage,
    UpdateTreatmentPlanV3AssessmentPagePayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentPage',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createTreatmentPlanV3AssessmentBehavior() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentBehavior,
    CreateTreatmentPlanV3AssessmentBehaviorPayload
  >(
    'treatmentPlanV3/createTreatmentPlanV3AssessmentBehavior',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/behaviors/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3AssessmentBehavior() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentBehavior,
    UpdateTreatmentPlanV3AssessmentBehaviorPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentBehavior',
    async ({ treatmentPlanId, behaviorId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/behaviors/${behaviorId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3AssessmentBehavior() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3AssessmentBehaviorPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3AssessmentBehavior',
    async ({ treatmentPlanId, behaviorId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/behaviors/${behaviorId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function createTreatmentPlanV3AssessmentMethod() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentMethod,
    CreateTreatmentPlanV3AssessmentMethodPayload
  >(
    'treatmentPlanV3/createTreatmentPlanV3AssessmentMethod',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3AssessmentMethod() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentMethod,
    UpdateTreatmentPlanV3AssessmentMethodPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentMethod',
    async ({ treatmentPlanId, methodId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3AssessmentMethod() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3AssessmentMethodPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3AssessmentMethod',
    async ({ treatmentPlanId, methodId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function updateTreatmentPlanV3AssessmentReadinessItem() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentReadinessItemUpdate,
    UpdateTreatmentPlanV3AssessmentReadinessItemPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentReadinessItem',
    async ({ treatmentPlanId, item, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/readiness/${item}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createTreatmentPlanV3AssessmentMethodVisuals() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentMethodVisual[],
    CreateTreatmentPlanV3AssessmentMethodVisualsPayload
  >(
    'treatmentPlanV3/createTreatmentPlanV3AssessmentMethodVisuals',
    async ({ treatmentPlanId, methodId, files, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/visuals/`,
          getAccessTokenSilently,
        },
        buildTreatmentPlanV3AssessmentMethodVisualsFormData(files),
        'multipart/form-data',
      ),
  );
}

function updateTreatmentPlanV3AssessmentMethodVisual() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentMethodVisual,
    UpdateTreatmentPlanV3AssessmentMethodVisualPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentMethodVisual',
    async ({ treatmentPlanId, methodId, visualId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/visuals/${visualId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3AssessmentMethodVisualFile() {
  return createAsyncThunk<
    ITreatmentPlanV3AssessmentMethodVisual,
    UpdateTreatmentPlanV3AssessmentMethodVisualFilePayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3AssessmentMethodVisualFile',
    async ({ treatmentPlanId, methodId, visualId, file, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/visuals/${visualId}/file/`,
          getAccessTokenSilently,
        },
        buildTreatmentPlanV3AssessmentMethodVisualFileFormData(file),
        'multipart/form-data',
      ),
  );
}

function deleteTreatmentPlanV3AssessmentMethodVisual() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3AssessmentMethodVisualPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3AssessmentMethodVisual',
    async ({ treatmentPlanId, methodId, visualId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/assessment/methods/${methodId}/visuals/${visualId}/`,
        getAccessTokenSilently,
      });
    },
  );
}
